/** Typed relay calls for recovery, trust and rotation. Responses are untrusted until verified. */
import {
  ContentDirectoryCatchUpPage,
  ContentRevokeResult,
  RecoveryBundle,
  RecoveryList,
  RecoveryRequest,
  RevocationReportList,
  SessionBindChallenge,
  UnlockCredential,
  UnlockHolders,
  UnlockList,
  UnlockMachines,
  UnlockStarted,
  UnlockStatus,
} from '@viko/protocol'
import { z } from 'zod'
import type { Api } from './api.js'

type KeysApi = Pick<Api, 'contentKeys'>

export const keysApi = (api: KeysApi) => ({
  catchUp: async (afterRevision: number, recipientId: string) =>
    ContentDirectoryCatchUpPage.parse(
      await api.contentKeys('directory/catchup', { afterRevision, recipientId, catchUp: true }),
    ),
  publishEscrow: (body: unknown) => api.contentKeys('escrow/publish', body),
  startRecovery: async (kind: 'recovery' | 'prf', recipientId?: string) =>
    RecoveryRequest.parse(
      await api.contentKeys('recovery/start', { kind, ...(recipientId ? { recipientId } : {}) }),
    ),
  listRecovery: async () => RecoveryList.parse(await api.contentKeys('recovery/list')),
  readRecovery: async (requestId: string) =>
    RecoveryBundle.parse(await api.contentKeys('recovery/read', { requestId })),
  decideRecovery: async (body: unknown) =>
    RecoveryRequest.parse(await api.contentKeys('recovery/decide', body)),
  completeRecovery: async (requestId: string) =>
    RecoveryRequest.parse(await api.contentKeys('recovery/complete', { requestId })),
  sessionChallenge: async () =>
    SessionBindChallenge.parse(await api.contentKeys('session/challenge')),
  bindSession: async (body: unknown) =>
    z.strictObject({ bound: z.string() }).parse(await api.contentKeys('session/bind', body)),
  revoke: async (revocations: unknown) =>
    ContentRevokeResult.parse(await api.contentKeys('revoke', { revocations })),
  revocationReports: async () =>
    RevocationReportList.parse(await api.contentKeys('revocation-reports')),
  // ---- unlock-via-mac ----
  unlockMachines: async () => UnlockMachines.parse(await api.contentKeys('unlock/machines')),
  /** Every key-holding machine with live state (additive; older hubs answer 404). */
  unlockHolders: async () => UnlockHolders.parse(await api.contentKeys('unlock/holders')),
  unlockRequest: async (signed: unknown) =>
    UnlockStarted.parse(await api.contentKeys('unlock/request', signed)),
  unlockStatus: async (requestId: string) =>
    UnlockStatus.parse(await api.contentKeys('unlock/status', { requestId })),
  unlockList: async () => UnlockList.parse(await api.contentKeys('unlock/list')),
  /** Pin bootstrap: one of the account's passkeys (advisory; verified locally before pinning). */
  unlockCredential: async (credentialId: string) =>
    UnlockCredential.parse(await api.contentKeys('unlock/credential', { credentialId })),
  // ---- end unlock-via-mac ----
})
export type KeysRelay = ReturnType<typeof keysApi>
